import React, { useCallback, useMemo } from 'react';
import {
  Anchor,
  Box,
  FilledCheckIcon,
  FilledLockIcon,
  Image,
  LargeCloseIcon,
  RawButton,
  Text,
  ThinCheckIcon,
  useFormatPhoneNumber,
  useResponsive,
} from '@common-fe/common-fe';
import _ from 'lodash';

import CIPIllustration from '@/assets/dashboard/cip-illustration.svg';
import GridBackground from '@/assets/new-investments/grid-background.svg';
import { Modal } from '@/components';
import InfoBlock from '@/modules/core/components/InfoBlock';
import { useAuthStore } from '@/modules/user';
import useOrganizationViewDataQuery from '@/modules/user/queries/useOrganizationViewData.query';

interface Props {
  closureDelayDays?: number;
  isVisible: boolean;
  onSetVisible: (value: boolean) => void;
  buttonNode?: React.ReactNode;
}

const CIPConfirmationModal: React.FC<Props> = ({ isVisible, onSetVisible, closureDelayDays, buttonNode }) => {
  const { isMobile } = useResponsive();
  const { user } = useAuthStore();
  const { formattedData: contactInfo } = useOrganizationViewDataQuery(user?.organization?.id);

  const isContactEmpty = useMemo(() => {
    if (!contactInfo && _.isEmpty(contactInfo)) {
      return true;
    }
    return false;
  }, [contactInfo]);

  const handleToggleVisible = useCallback(() => {
    onSetVisible(!isVisible);
  }, [isVisible, onSetVisible]);

  const formattedPhoneNumber = useFormatPhoneNumber(contactInfo?.customerServiceNumber || '');

  return (
    <>
      <Modal
        visible={isVisible}
        testId="dashboard_statistics_cip-modal"
        onSetVisible={handleToggleVisible}
        contentStyle={{
          padding: 0,
        }}
      >
        <Box
          style={{
            position: 'relative',
          }}
          overflow="hidden"
          round="moduleRound"
        >
          <Image
            src={GridBackground}
            style={{
              position: 'absolute',
              left: -20,
              top: -20,
              zIndex: 1,
            }}
          />
          <Box
            style={{
              zIndex: 2,
            }}
          >
            <Box pad="m" direction="row" align="center" justify="end">
              <RawButton
                data-testid="dashboard_statistics_cip-modal_close-button"
                style={{ height: '24px' }}
                onClick={handleToggleVisible}
              >
                <LargeCloseIcon color="iconPrimary" size="24px"></LargeCloseIcon>
              </RawButton>
            </Box>
            <Box direction="column" align="center" justify="center">
              <Box>
                <Text
                  data-testid="dashboard_statistics_cip-modal_title"
                  color="textTitle"
                  size={isMobile ? '3xl' : '4xl'}
                  textAlign="center"
                  weight={700}
                >
                  Verify Identity
                </Text>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box direction="column" margin={{ top: 'spacing32' }} pad={{ horizontal: 'l' }}>
          <Box
            direction="column"
            align="center"
            overflow="hidden"
            round="container2Round"
            pad={{ top: '54px', horizontal: 'xl' }}
            background="background1"
          >
            <Text size="3xl" textAlign="center" weight={700} color="textOnColor">
              Why We’re Asking
            </Text>
            <Text margin={{ top: 'spacing12' }} weight={400} textAlign="center" color="textOnColor">
              To open your HSA, we’re required to verify identity. This helps protect your account and is part
              of federal requirements under the USA PATRIOT Act.
            </Text>
            <Box fill margin={{ top: 'spacing24' }} width={{ max: '280px' }}>
              {buttonNode}
            </Box>
            <Box margin={{ top: 'spacing24' }}>
              <Image width="280px" src={CIPIllustration} />
            </Box>
          </Box>
          <Box direction={isMobile ? 'column' : 'row'} margin={{ top: isMobile ? 'spacing12' : 'spacing8' }}>
            <Box
              direction="column"
              align="center"
              round="container2Round"
              pad={{ horizontal: 'xl', vertical: 'xl' }}
              background="whatIsNew2"
              margin={{ bottom: isMobile ? 'spacing12' : undefined }}
              style={{ flex: 1 }}
            >
              <Box>
                <FilledCheckIcon size="72px" color="iconOnColor" />
                <Text
                  data-testid="dashboard_statistics_cip-modal_required-action-title"
                  size="3xl"
                  margin={{ top: 'spacing16' }}
                  color="textOnColor"
                  weight={700}
                >
                  Required Action
                </Text>
                <Text
                  weight={400}
                  margin={{ top: 'spacing16' }}
                  color="textOnColor"
                  style={{ lineHeight: '20px' }}
                >
                  Answer a few quick questions about yourself, or upload an identification document.
                  <br />
                  Both options are fast and secure.
                </Text>
              </Box>
            </Box>
            <Box pad={{ left: 's' }}></Box>
            <Box
              align="center"
              pad={{ horizontal: 'xl', vertical: 'xl' }}
              round="container2Round"
              background="whatIsNew1"
              style={{ flex: 1 }}
            >
              <Box direction="column">
                <FilledLockIcon size="72px" color="iconOnColor" />
                <Text
                  data-testid="dashboard_statistics_cip-modal_why-matters-title"
                  size="3xl"
                  margin={{ top: 'spacing16' }}
                  color="textOnColor"
                  weight={700}
                >
                  Why This Matters
                </Text>
                <Box direction="row" margin={{ top: 'spacing12' }}>
                  <ThinCheckIcon color="iconOnColor" />
                  <Text weight={400} size="medium" color="textOnColor">
                    Required by law to open your account
                  </Text>
                </Box>

                <Box direction="row" margin={{ top: 'spacing6' }}>
                  <ThinCheckIcon color="iconOnColor" />
                  <Text weight={400} size="medium" color="textOnColor">
                    Help keep your information safe
                  </Text>
                </Box>
                <Box direction="row" margin={{ top: 'spacing6' }}>
                  <ThinCheckIcon color="iconOnColor" />
                  <Text weight={400} size="medium" color="textOnColor">
                    Takes a minute to complete
                  </Text>
                </Box>
                <Box direction="row" margin={{ top: 'spacing6' }}>
                  <ThinCheckIcon color="iconOnColor" />
                  <Text weight={400} size="medium" color="textOnColor">
                    Allows you to receive any employer contributions
                  </Text>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box direction="column" pad="l">
          <Box margin={{ bottom: 's' }}>
            <InfoBlock testId="1" title="Is this process secure?">
              Yes. All information is encrypted and protected.
            </InfoBlock>
          </Box>
          <Box margin={{ bottom: 's' }}>
            <InfoBlock testId="2" title="How long does this take?">
              Less than a minute in most cases.
            </InfoBlock>
          </Box>
          {(!!closureDelayDays || closureDelayDays == 0) && (
            <Box margin={{ bottom: 's' }}>
              <InfoBlock testId="3" title="What happens if this isn’t completed?">
                We can’t open the employee’s HSA, and they’ll miss out on any employer contributions. After
                {` ${closureDelayDays}`} days with no action, we’ll no longer be able to open the account.
              </InfoBlock>
            </Box>
          )}

          {!isContactEmpty && (
            <Box margin={{ bottom: 's' }}>
              <InfoBlock testId="6" title="Who do I contact if I have more questions?">
                <Box direction="column">
                  {contactInfo?.customerServiceNumber && (
                    <Anchor href={`tel:${contactInfo?.customerServiceNumber}`} target="_blank">
                      <Text color="textBody" weight={400}>
                        {formattedPhoneNumber}
                      </Text>
                    </Anchor>
                  )}

                  {contactInfo?.customerServiceEmail && (
                    <Anchor href={`mailto:${contactInfo?.customerServiceEmail}`} target="_blank">
                      <Text color="textBody" weight={400} style={{ textDecoration: 'underline' }}>
                        {contactInfo?.customerServiceEmail}
                      </Text>
                    </Anchor>
                  )}
                  {contactInfo?.customerServiceUrlLink && (
                    <Anchor href={contactInfo?.customerServiceUrlLink} target="_blank">
                      <Text color="textBody" weight={400} style={{ textDecoration: 'underline' }}>
                        {contactInfo?.customerServiceUrlName || contactInfo?.customerServiceUrlLink}
                      </Text>
                    </Anchor>
                  )}
                </Box>
              </InfoBlock>
            </Box>
          )}
          <Box margin={{ top: 'spacing12' }}>{buttonNode}</Box>
        </Box>
      </Modal>
    </>
  );
};
export default CIPConfirmationModal;
